import type {HourOfTheDay, Minute, MinuteOfTheHour} from '../Time';
import type {DayOfTheWeek} from '../util/DayOfTheWeek';
import subtract from '../util/subtract';
import type {ScheduleRecords} from './ScheduleRecords';

export type UpcomingEvent = {
  kind: 'ongoing' | 'upcoming';
  day: DayOfTheWeek;
  start: UpcomingEventStart;
};

export type UpcomingEventStart = {
  hour: HourOfTheDay;
  minute: MinuteOfTheHour;
};

export default function getUpcomingEvent(
  day: number,
  hour: number,
  minute: number,
  records: ScheduleRecords,
): UpcomingEvent | undefined {
  const current = createMinuteOfTheWeek(day, hour, minute);
  let minWait = Infinity as Minute;
  let upcoming: UpcomingEvent | undefined;
  for (const record of records) {
    const start = createMinuteOfTheWeek(
      record.day,
      record.start.hour,
      record.start.minute,
    );
    const end = start + record.duration;
    const wait =
      current <= start
        ? subtract(start, current)
        : subtract(MINUTES_IN_A_WEEK, subtract(current, start));
    if (wait < minWait) {
      minWait = wait;
      upcoming = {
        day: record.day,
        start: {...record.start} as UpcomingEventStart,
        kind: 'upcoming',
      };
    }
    if (start <= current && current < end) {
      return {
        day: record.day,
        start: {...record.start} as UpcomingEventStart,
        kind: 'ongoing',
      };
    }
    if (MINUTES_IN_A_WEEK < end) {
      const warpStart = 0;
      const warpEnd = end % MINUTES_IN_A_WEEK;
      if (warpStart <= current && current < warpEnd) {
        return {
          day: record.day,
          start: {...record.start} as UpcomingEventStart,
          kind: 'ongoing',
        };
      }
    }
  }
  return upcoming;
}

function createMinuteOfTheWeek(
  day: DayOfTheWeek,
  hour: number,
  minute: number,
): Minute {
  return (minute + hour * 60 + day * 24 * 60) as Minute;
}

const MINUTES_IN_A_WEEK = (7 * 24 * 60) as Minute;
